<template>
  <div class="va-data-table">
    <va-inner-loading :loading="loading">
      <vuetable
        ref="vuetable"
        :api-mode="false"
        :fields="fields"
        :data="apiMode ? data : undefined"
        :data-manager="apiMode ? undefined : dataManager"
        :pagination-path="apiMode ? '' : 'pagination'"
        :no-data-template="noDataLabel"
        :css="styles"
        :row-class="rowClass"
        :track-by="trackBy"
        @vuetable:row-clicked="rowClicked"
        :detail-row-component="detailRowComponent"
      >
        <!-- https://stackoverflow.com/questions/50891858/vue-how-to-pass-down-slots-inside-wrapper-component   -->
        <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
          <slot :name="slot" v-bind="scope" />
        </template>
      </vuetable>

      <div v-if="!noPagination && paginationTotal > 1" class="va-data-table__pagination">
        <va-pagination
          v-model="currentPage"
          :pages="paginationTotal"
          :visible-pages="visiblePages"
          :boundary-links="paginationTotal > visiblePages"
          @input="inputPage"
        />
      </div>
    </va-inner-loading>
  </div>
</template>

<script>
import { SpringSpinner } from "epic-spinners";
import Vuetable from "vuetable-2/src/components/Vuetable";
import VaPagination from "vuestic-ui/src/components/vuestic-components/va-pagination/VaPagination";
import VaInnerLoading from "vuestic-ui/src/components/vuestic-components/va-inner-loading/VaInnerLoading";

export default {
  name: "va-custom-table",
  components: {
    VaInnerLoading,
    SpringSpinner,
    Vuetable,
    VaPagination,
  },
  props: {
    detailRowComponent: {
      type: String,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    perPage: {
      type: Number,
      default: 6,
    },
    visiblePages: {
      type: Number,
      default: 4,
    },
    trackBy: {
      type: String,
      default: "id",
    },
    apiMode: Boolean,
    clickable: Boolean,
    hoverable: Boolean,
    noPagination: Boolean,
    noDataLabel: {
      type: String,
      default: undefined,
    },
    rowClass: {
      type: Function,
      default: undefined,
    },
    totalPages: {
      type: Number,
      default: 0,
    },
    loading: Boolean,
  },
  data() {
    return {
      currentPage: 1,
    };
  },
  computed: {
    styles() {
      return {
        tableClass: this.buildTableClass(),
        ascendingIcon: "fa fa-caret-up",
        descendingIcon: "fa fa-caret-down",
        renderIcon: (classes) => {
          return '<span class="' + classes.join(" ") + '"></span>';
        },
      };
    },
    paginationTotal() {
      return this.apiMode
        ? this.totalPages
        : Math.ceil(this.data.length / this.perPage);
    },
  },
  watch: {
    perPage() {
      this.refresh();
    },
    data() {
      this.refresh();
    },
    fields() {
      this.refreshFields(); // support dynamic translations
    },
  },
  methods: {
    buildTableClass() {
      let name = "va-data-table__vuetable va-table va-table--striped";

      if (this.clickable) {
        name += " va-table--clickable";
      }

      if (this.hoverable) {
        name += " va-table--hoverable";
      }

      return name;
    },
    dataManager(sortOrder, pagination) {
      let sorted = [];

      if (!sortOrder.length) {
        sorted = this.data;
      } else {
        const { sortField, direction } = sortOrder[0];
        sorted =
          direction === "asc"
            ? this.sortAsc(this.data, sortField)
            : this.sortDesc(this.data, sortField);
      }

      if (this.noPagination) {
        return {
          data: sorted,
        };
      }

      pagination = this.buildPagination(sorted.length, this.perPage);
      const { from } = pagination;
      const sliceFrom = from - 1;

      return {
        pagination,
        data: sorted.slice(sliceFrom, sliceFrom + this.perPage),
      };
    },
    sortAsc(items, field) {
      return items.slice().sort((a, b) => {
        return a[field].localeCompare(b[field]);
      });
    },
    sortDesc(items, field) {
      return items.slice().sort((a, b) => {
        return b[field].localeCompare(a[field]);
      });
    },
    buildPagination(l, perPage) {
      return this.$refs.vuetable.makePagination(l, perPage);
    },
    inputPage(page) {
      if (this.apiMode) {
        this.$emit("page-selected", page);
        return;
      }

      this.$refs.vuetable.changePage(page);
    },
    refresh() {
      this.$refs.vuetable.refresh();
    },
    refreshFields() {
      this.$nextTick(() => {
        this.$refs.vuetable.normalizeFields();
      });
    },

    detailRowClicked(element, event) {
      console.log("test", a, b, c);
    },
    rowClicked(row) {
      this.$emit("row-clicked", row);
    },
    openRow(id) {
      this.$refs.vuetable.toggleDetailRow(id);
    },
    isVisibleDetailRow(id) {
      return this.$refs.vuetable.isVisibleDetailRow(id);
    },
  },
};
</script>

<style lang="scss">
@import "./vuestic-sass/resources/resources";

.va-data-table {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow-x: auto;

  &__vuetable {
    width: 100%;

    th {
      &.sortable {
        color: $brand-primary;
      }

      .sort-icon {
        font-size: 0.625rem;
      }
    }

    .vuetable-empty-result {
      padding: 4.5rem 1rem;
      font-size: 1rem;
      color: $gray;
    }
  }

  &__pagination {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
  }
}
</style>
